import React, { useEffect, useState } from "react";
import { forceSyncNow, getSyncStatus } from "../services/sync";

interface SyncStatusState {
  isOnline: boolean;
  isSyncing: boolean;
  pendingCount: number;
  lastSync?: string;
}

const SyncStatus: React.FC = () => {
  const [status, setStatus] = useState<SyncStatusState>({
    isOnline: navigator.onLine,
    isSyncing: false,
    pendingCount: 0,
  });
  const [isExpanded, setIsExpanded] = useState(false);

  // Update status
  const updateStatus = async () => {
    try {
      const syncStatus = await getSyncStatus();
      setStatus((prev) => ({
        ...prev,
        isOnline: syncStatus.isOnline,
        pendingCount:
          syncStatus.pendingTasks +
          syncStatus.pendingPhotos +
          syncStatus.pendingAudio,
      }));
    } catch (error) {
      console.error("Failed to get sync status:", error);
    }
  };

  // Handle sync button click
  const handleSyncClick = async () => {
    if (!status.isOnline) {
      return;
    }

    try {
      setStatus((prev) => ({ ...prev, isSyncing: true }));
      await forceSyncNow();
      await updateStatus();
    } catch (error) {
      console.error("Manual sync failed:", error);
    } finally {
      setStatus((prev) => ({ ...prev, isSyncing: false }));
    }
  };

  useEffect(() => {
    // Initial status update
    updateStatus();

    // Listen for sync events
    const handleSyncCompleted = () => {
      setStatus((prev) => ({
        ...prev,
        isSyncing: false,
        lastSync: new Date().toISOString(),
      }));
      updateStatus();
    };

    // Listen for online/offline events
    const handleOnline = () => {
      setStatus((prev) => ({ ...prev, isOnline: true }));
      updateStatus();
    };

    const handleOffline = () => {
      setStatus((prev) => ({ ...prev, isOnline: false }));
    };

    window.addEventListener("sync:completed", handleSyncCompleted);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Update status periodically
    const interval = setInterval(updateStatus, 30000); // Every 30 seconds

    return () => {
      window.removeEventListener("sync:completed", handleSyncCompleted);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      clearInterval(interval);
    };
  }, []);

  // Status icon and color
  const getStatusIndicator = () => {
    if (!status.isOnline) {
      return {
        icon: (
          <svg
            className="w-3 h-3 sm:w-4 sm:h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M18.364 5.636l-12.728 12.728m0-12.728l12.728 12.728"
            />
          </svg>
        ),
        color: "text-red-600 bg-red-100",
        label: "Offline",
      };
    }

    if (status.isSyncing) {
      return {
        icon: (
          <svg
            className="w-3 h-3 sm:w-4 sm:h-4 animate-spin"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        ),
        color: "text-blue-600 bg-blue-100",
        label: "Syncing...",
      };
    }

    if (status.pendingCount > 0) {
      return {
        icon: (
          <svg
            className="w-3 h-3 sm:w-4 sm:h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.082 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
        ),
        color: "text-yellow-600 bg-yellow-100",
        label: `${status.pendingCount} pending`,
      };
    }

    return {
      icon: (
        <svg
          className="w-3 h-3 sm:w-4 sm:h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 13l4 4L19 7"
          />
        </svg>
      ),
      color: "text-green-600 bg-green-100",
      label: "Synced",
    };
  };

  const statusIndicator = getStatusIndicator();

  return (
    <div className="relative">
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
        {/* Status indicator */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center space-x-1 sm:space-x-2 px-2 py-1.5 sm:px-3 sm:py-2 hover:bg-gray-50 transition-colors w-full text-left"
        >
          <div
            className={`flex items-center justify-center w-4 h-4 sm:w-6 sm:h-6 rounded-full ${statusIndicator.color}`}
          >
            {statusIndicator.icon}
          </div>
          <span className="text-xs sm:text-sm font-medium text-gray-900 hidden sm:inline">
            {statusIndicator.label}
          </span>
          <svg
            className={`w-3 h-3 sm:w-4 sm:h-4 text-gray-400 transition-transform ${
              isExpanded ? "transform rotate-180" : ""
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        {/* Expanded details */}
        {isExpanded && (
          <div className="absolute top-full right-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
            <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
              <div className="space-y-2 text-xs text-gray-600">
                <div className="flex justify-between">
                  <span>Status:</span>
                  <span
                    className={
                      status.isOnline ? "text-green-600" : "text-red-600"
                    }
                  >
                    {status.isOnline ? "Online" : "Offline"}
                  </span>
                </div>

                {status.pendingCount > 0 && (
                  <div className="flex justify-between">
                    <span>Pending items:</span>
                    <span className="text-yellow-600">
                      {status.pendingCount}
                    </span>
                  </div>
                )}

                {status.lastSync && (
                  <div className="flex justify-between">
                    <span>Last sync:</span>
                    <span>
                      {new Date(status.lastSync).toLocaleTimeString()}
                    </span>
                  </div>
                )}
              </div>

              {/* Sync button */}
              {status.isOnline && !status.isSyncing && (
                <button
                  onClick={handleSyncClick}
                  className="w-full mt-2 px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors"
                >
                  Sync Now
                </button>
              )}

              {/* Syncing indicator */}
              {status.isSyncing && (
                <div className="w-full mt-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded text-center">
                  Syncing...
                </div>
              )}

              {/* Offline message */}
              {!status.isOnline && (
                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                  Tasks will be saved locally and synced when connection is
                  restored.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SyncStatus;
